import { useCallback, useMemo, useState } from 'react';
import type { ThemeSpec } from '../themes';
import { useTool } from '../state/tool';
import { AreaEraseIcon, ChalkIcon, EraserIcon, PenIcon, DownloadIcon, UndoIcon, FitIcon } from './icons';

interface Props {
  theme: ThemeSpec;
  isAdmin: boolean;
  boardName?: string;
  onEnterAreaErase?: () => void;
  onUndo?: () => void;
  canUndo?: boolean;
  onFit?: () => void;
}

/** `input[type=color]` is unsupported on iOS Safari — fall back to a hex popover. */
const NATIVE_COLOR_SUPPORTED = (() => {
  if (typeof document === 'undefined') return false;
  const i = document.createElement('input');
  i.setAttribute('type', 'color');
  return i.type === 'color';
})();

export function Toolbar({ theme, isAdmin, boardName, onEnterAreaErase, onUndo, canUndo, onFit }: Props) {
  const { mode, color, sizeIdx, setMode, setColor, setSize } = useTool();
  const [isExporting, setIsExporting] = useState(false);

  const isChalk = theme.tool === 'chalk';
  const ToolIcon = isChalk ? ChalkIcon : PenIcon;
  const toolLabel = isChalk ? '粉笔' : '马克笔';

  const [showColorInput, setShowColorInput] = useState(false);
  const [colorDraft, setColorDraft] = useState(color);

  const currentSizePx = Math.round(
    theme.sizes[0] + (theme.sizes[2] - theme.sizes[0]) * (sizeIdx - 1) / 9
  );

  const onColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (/^#[0-9a-fA-F]{6}$/.test(v)) setColor(v);
      else if (/^#[0-9a-fA-F]{3}$/.test(v)) {
        setColor(`#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`);
      }
    },
    [setColor],
  );

  const handleExport = useCallback(() => {
    if (isExporting) return;
    const bg = document.getElementById('canvas-bg') as HTMLCanvasElement | null;
    const st = document.getElementById('canvas-static') as HTMLCanvasElement | null;
    const lv = document.getElementById('canvas-live') as HTMLCanvasElement | null;
    if (!bg || !st || !lv) return;
    setIsExporting(true);
    const w = bg.width;
    const h = bg.height;
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d')!;
    ctx.drawImage(bg, 0, 0);
    ctx.drawImage(st, 0, 0);
    ctx.drawImage(lv, 0, 0);
    const slug = (boardName ?? '南哪小黑板').replace(/[\\/:*?"<>|]/g, '').slice(0, 40) || '南哪小黑板';
    out.toBlob((blob) => {
      try {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${slug}_${new Date().toISOString().slice(0, 10)}.png`;
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        setIsExporting(false);
      }
    }, 'image/png');
  }, [isExporting, boardName]);

  const divider = (
    <span
      aria-hidden
      className="hidden sm:block w-px h-6 self-center"
      style={{ background: theme.divider }}
    />
  );

  // iOS Safari has no native color input — keep the hex popover there.
  const colorInPalette = theme.palette.includes(color.toLowerCase());

  const nativeColorValue = useMemo(
    () => (/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#ffffff'),
    [color]
  );

  return (
    <div
      role="toolbar"
      aria-label="绘图工具"
      className="bb-toolbar absolute bottom-3 xs:bottom-5 left-1/2 -translate-x-1/2 flex flex-wrap justify-center gap-1 sm:gap-1.5 items-center px-2 xs:px-2.5 pt-1.5 rounded-xl xs:rounded-2xl backdrop-blur-xl max-w-[calc(100vw-16px)]"
      style={{
        background: theme.surface,
        color: theme.surfaceText,
        border: `1px solid ${theme.surfaceBorder}`,
        boxShadow: theme.surfaceShadow,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6px)',
      }}
    >
      <ToolButton
        theme={theme}
        label={toolLabel}
        active={mode === 'pen'}
        onClick={() => setMode('pen')}
      >
        <ToolIcon />
      </ToolButton>

      <ToolButton
        theme={theme}
        label="橡皮"
        active={mode === 'eraser'}
        onClick={() => setMode('eraser')}
      >
        <EraserIcon />
      </ToolButton>

      <ToolButton
        theme={theme}
        label="撤销上一笔 (Ctrl+Z)"
        disabled={!canUndo}
        onClick={() => onUndo?.()}
      >
        <UndoIcon />
      </ToolButton>

      <ToolButton
        theme={theme}
        label="缩放到全部内容 (F)"
        onClick={() => onFit?.()}
      >
        <FitIcon />
      </ToolButton>

      {divider}

      {/* Brush size slider */}
      <div className="flex items-center gap-2 min-w-[80px]">
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={sizeIdx}
          onChange={(e) => setSize(parseInt(e.target.value, 10))}
          aria-label={`画笔粗细 ${currentSizePx}px`}
          className="w-16 h-1.5 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${theme.selectedRing} ${(sizeIdx - 1) * 100 / 9}%, ${theme.surfaceBorder} ${(sizeIdx - 1) * 100 / 9}%)`,
            accentColor: theme.selectedRing,
          }}
        />
        <span
          className="block rounded-full transition-transform duration-150 flex-shrink-0"
          style={{
            width: Math.max(4, currentSizePx),
            height: Math.max(4, currentSizePx),
            background: color,
          }}
        />
      </div>

      {divider}

      <button
        onClick={handleExport}
        disabled={isExporting}
        aria-label={isExporting ? '正在保存图片' : '保存图片'}
        aria-busy={isExporting}
        title={isExporting ? '正在保存…' : '保存图片'}
        className="w-10 h-10 rounded-xl flex items-center justify-center disabled:cursor-wait"
        style={{
          background: theme.hoverBg,
          color: theme.surfaceText,
          opacity: isExporting ? 0.7 : 1,
        }}
        onMouseEnter={(e) => { if (!isExporting) e.currentTarget.style.background = theme.pressedBg; }}
        onMouseLeave={(e) => { if (!isExporting) e.currentTarget.style.background = theme.hoverBg; }}
      >
        {isExporting ? (
          <span
            aria-hidden
            className="block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
          />
        ) : (
          <DownloadIcon />
        )}
      </button>

      {divider}

      {/* Colors — their own row on phones, inline on ≥sm */}
      <div
        className="w-full sm:w-auto flex items-center justify-center gap-1.5 overflow-x-auto py-0.5"
        role="radiogroup"
        aria-label="颜色"
        style={{ scrollbarWidth: 'none' }}
      >
        {theme.palette.map((c) => {
          const active = color === c;
          return (
            <button
              key={c}
              aria-label={`颜色 ${c}`}
              aria-checked={active}
              role="radio"
              title={c}
              onClick={() => { setColor(c); setShowColorInput(false); }}
              className="w-7 h-7 rounded-full flex-shrink-0"
              style={{
                background: c,
                transform: active && !showColorInput ? 'scale(1.16)' : 'scale(1)',
                boxShadow: active && !showColorInput
                  ? `0 0 0 2px ${theme.surface}, 0 0 0 4px ${theme.selectedRing}`
                  : `inset 0 0 0 1px ${theme.surfaceBorder}`,
              }}
            />
          );
        })}
        {/* Custom color: native picker where supported, hex popover elsewhere */}
        <span
          className="relative w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-mono border border-dashed"
          style={{
            borderColor: theme.surfaceBorder,
            color: theme.surfaceTextMuted,
            background: colorInPalette ? 'transparent' : theme.selectedBg,
          }}
        >
          {NATIVE_COLOR_SUPPORTED ? (
            <>
              {!colorInPalette && (
                <span
                  aria-hidden
                  className="absolute inset-[5px] rounded-full"
                  style={{ background: color }}
                />
              )}
              <span aria-hidden className="relative">#</span>
              <input
                type="color"
                aria-label="自定义颜色"
                value={nativeColorValue}
                onChange={(e) => setColor(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </>
          ) : (
            <button
              aria-label="自定义颜色"
              aria-expanded={showColorInput}
              onClick={() => setShowColorInput(!showColorInput)}
              className="w-full h-full flex items-center justify-center"
            >
              #
            </button>
          )}
        </span>
      </div>
      {showColorInput && !NATIVE_COLOR_SUPPORTED && (
        <div className="flex items-center gap-1">
          <span
            className="w-5 h-5 rounded-full flex-shrink-0 border"
            style={{
              background: /^#[0-9a-fA-F]{6}$/.test(colorDraft) ? colorDraft : color,
              borderColor: theme.surfaceBorder,
            }}
          />
          {(() => {
            const draftValid =
              colorDraft === '' ||
              /^#[0-9a-fA-F]{0,6}$/.test(colorDraft) === false
                ? false
                : true;
            const isCommittable = /^#[0-9a-fA-F]{3}$/.test(colorDraft) || /^#[0-9a-fA-F]{6}$/.test(colorDraft);
            const showError = colorDraft !== '' && !draftValid;
            const borderColor = showError
              ? '#ef4444'
              : isCommittable
                ? theme.selectedRing
                : theme.surfaceBorder;
            return (
              <input
                type="text"
                value={colorDraft}
                onChange={(e) => {
                  setColorDraft(e.target.value);
                  onColorChange(e);
                }}
                onBlur={() => {
                  if (/^#[0-9a-fA-F]{3,6}$/.test(colorDraft)) setColor(colorDraft);
                  else setColorDraft(color);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                placeholder="#fff 或 #ffffff"
                maxLength={7}
                aria-label="自定义颜色 hex"
                aria-invalid={showError || undefined}
                className="w-[66px] h-7 px-1.5 rounded-md text-[11px] font-mono border"
                style={{
                  background: theme.surface,
                  color: showError ? '#ef4444' : theme.surfaceText,
                  borderColor,
                }}
                autoFocus
              />
            );
          })()}
        </div>
      )}

      {isAdmin && onEnterAreaErase && (
        <>
          {divider}
          {(() => {
            const isLightSurface = theme.surfaceText === '#111827';
            const cls = isLightSurface
              ? 'bg-red-600/10 text-red-700 hover:bg-red-600/20'
              : 'bg-red-500/15 text-red-300 hover:bg-red-500/25';
            return (
              <button
                onClick={onEnterAreaErase}
                aria-label="管理员区域擦除 (Shift+E)"
                title="管理员区域擦 (Shift+E)"
                className={`flex items-center gap-1.5 pl-2.5 pr-3 h-9 rounded-xl text-sm font-medium ${cls}`}
              >
                <AreaEraseIcon size={18} />
                <span>区域擦</span>
              </button>
            );
          })()}
        </>
      )}
    </div>
  );
}

function ToolButton({
  theme,
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  theme: ThemeSpec;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="relative w-10 h-10 rounded-xl flex items-center justify-center disabled:opacity-35 disabled:cursor-default"
      style={{
        background: active ? theme.selectedBg : 'transparent',
        color: active ? theme.selectedRing : theme.surfaceText,
      }}
      onMouseEnter={(e) => {
        if (!active && !disabled) e.currentTarget.style.background = theme.hoverBg;
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
      {active && (
        <span
          aria-hidden
          className="absolute -bottom-[7px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
          style={{ background: theme.selectedRing }}
        />
      )}
    </button>
  );
}
